'use client';

import { Link } from '@/i18n/navigation';
import Button from '@/ui/Button/Button';
import { logoProps, useLinks } from '@/ui/layout/NavBar/navbar-config';
import List from '@/ui/List/List';
import Logo from '@/ui/Logo';
import clsx from 'clsx';
import { ArrowRight, MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { CSSProperties } from 'react';
import LanguageSelector from './LanguageSelector';
import styles from './NavBar.module.scss';
import ThemeSwitcher from './ThemeSwitcher';

const labelStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '2em',
  alignItems: 'center',
  marginBottom: '2em',
};

const Menu: React.FC<{
  open?: boolean;
  pathname: string;
  onNavigate?: React.MouseEventHandler;
}> = ({ open, pathname, onNavigate }) => {
  const t = useTranslations('layout.navbar');
  const links = useLinks({ home: true, kitchensink: process.env.NODE_ENV === 'development' });

  return (
    <div className={clsx(styles.menu, { [styles.active]: open })}>
      <label htmlFor="theme-switcher" style={labelStyles}>
        <h2 className={styles.menuListTitle}>{t('dark-mode')}</h2>
        <ThemeSwitcher id="theme-switcher" />
      </label>
      <h3 className={styles.menuListTitle}>{t('explore')}</h3>
      <List separator={true} className={styles.links}>
        {links.map(({ href, text }) => (
          <Link
            key={href}
            href={href}
            className={clsx(styles.listItem, { [styles.active]: href === pathname })}
            onClick={onNavigate}
          >
            {text}
            <ArrowRight style={{ marginLeft: 'auto' }} className={styles.icon} />
          </Link>
        ))}
      </List>
      <div className={styles.bottomNav}>
        <label htmlFor="language-switcher" style={labelStyles}>
          <h2 className={styles.menuListTitle}>{t('language')}</h2>
          <LanguageSelector id="language-switcher" anchor="bottom right" width="max-content" />
        </label>
        <div style={{ width: '100%', marginTop: 16 }}>
          <Button
            color="secondary"
            href="/contact"
            style={{ width: '100%' }}
            onClick={onNavigate}
            data-umami-event="contact-me-header"
          >
            {t('contact-me')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const MenuButton: React.FC<{ open?: boolean; onToggle?: (open: boolean) => void }> = ({
  open,
  onToggle,
}) => (
  <Button
    variant="outlined"
    color="neutral"
    style={{
      border: 'none',
      padding: 0,
      width: 32,
      height: 32,
      boxSizing: 'content-box',
      marginRight: 8,
    }}
    onClick={() => onToggle?.(!open)}
  >
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          key="close"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <XIcon key="close" />
        </motion.div>
      )}
      {!open && (
        <motion.div
          key="open"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <MenuIcon key="open" size={32} />
        </motion.div>
      )}
    </AnimatePresence>
  </Button>
);

export type NavBarContentMobileProps = {
  pathname: string;
  logoVariant?: 'light' | 'dark';
  open?: boolean;
  onToggle?: (open: boolean) => void;
};

const NavBarContentMobile: React.FC<NavBarContentMobileProps> = ({
  pathname,
  open,
  logoVariant,
  onToggle,
}) => {
  return (
    <>
      <div className={clsx(styles.content, { [styles.open]: open })}>
        <Logo className={styles.logo} variant={logoVariant} {...logoProps} />

        <div className={styles.rightNav}>
          <MenuButton open={open} onToggle={onToggle} />
        </div>
      </div>
      <motion.div
        className={styles.menuBase}
        initial={{ height: 0 }}
        animate={{ height: open ? '100%' : 0 }}
        style={{ boxShadow: open ? '0 0 30px #0005' : 'none' }}
      >
        <Menu open={open} pathname={pathname} onNavigate={() => onToggle?.(false)} />
      </motion.div>
    </>
  );
};

export default NavBarContentMobile;
